import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { ArrowRight, Building2 } from 'lucide-react';
import PageHero from '../../components/ui/page-hero';
import CaseStudyCard from '../../components/case-studies/CaseStudyCard';
import CaseStudyStats from '../../components/case-studies/CaseStudyStats';
import StickyCTA from '../../components/case-studies/StickyCTA';
import caseStudiesData from '../../content/case-studies/case-studies.json';
import '../../components/services/services.css';

const CaseStudies = () => {
  useEffect(() => {
    // Smooth scroll behavior for better UX
    document.documentElement.style.scrollBehavior = 'smooth';
    
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{caseStudiesData.meta.title}</title>
        <meta name="description" content={caseStudiesData.meta.description} />
        <meta name="keywords" content={caseStudiesData.meta.keywords} />
        <meta property="og:title" content={caseStudiesData.meta.title} />
        <meta property="og:description" content={caseStudiesData.meta.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://envael.com/case-studies" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={caseStudiesData.meta.title} />
        <meta name="twitter:description" content={caseStudiesData.meta.description} />
        <link rel="canonical" href="https://envael.com/case-studies" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Page Hero with Navigation */}
        <PageHero 
          pageName="Case Studies" 
          subtitle={caseStudiesData.hero.subtitle}
          size="large"
          fullHeight={true}
          enableSpline={true}
          ctaButtons={caseStudiesData.hero.cta}
        />

        {/* Project Stats */}
        <CaseStudyStats stats={caseStudiesData.stats} />

        {/* Case Studies */}
        {caseStudiesData.caseStudies.map((caseStudy, index) => (
          <CaseStudyCard 
            key={caseStudy.id} 
            caseStudy={caseStudy} 
            index={index}
          />
        ))}

        {/* Final CTA Section */}
        <section className="py-24 bg-gradient-to-br from-primary/5 via-background to-primary/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Ready to Create Your Success Story?
              </h2>
              <p className="text-xl text-muted-foreground mb-12 leading-relaxed">
                Join 50+ companies that have transformed their operations with our solutions. 
                Let's discuss your project and achieve similar results.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <button className="px-8 py-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold text-lg group">
                  Start Your Project
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform inline" />
                </button>
                
                <button className="px-8 py-4 border-2 border-primary/20 text-primary rounded-lg hover:bg-primary/5 transition-colors font-semibold text-lg">
                  View All Projects
                </button>
              </div>

              {/* Success Indicators */}
              <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">50+</div>
                  <div className="text-sm text-muted-foreground">Projects Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">98%</div>
                  <div className="text-sm text-muted-foreground">Client Satisfaction</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">45%</div>
                  <div className="text-sm text-muted-foreground">Average Improvement</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">6</div>
                  <div className="text-sm text-muted-foreground">Industries Served</div>
                </div>
              </div>

              {/* Client Logos/Testimonials */}
              <div className="mt-16">
                <h3 className="text-2xl font-bold text-foreground mb-8">Trusted by Leading Companies</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center opacity-60">
                  {[
                    'Maroc Telecom',
                    'Attijariwafa Bank',
                    'Majid Al Futtaim',
                    'Royal Air Maroc',
                    'ONCF',
                    'Maroc Poste'
                  ].map((company, index) => (
                    <div key={index} className="text-center p-4 rounded-lg bg-muted/30 border border-border/50">
                      <Building2 className="h-8 w-8 text-primary mx-auto mb-2" />
                      <div className="text-sm font-medium text-foreground">{company}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sticky CTA */}
        <StickyCTA 
          title={caseStudiesData.stickyCta.title}
          buttons={caseStudiesData.stickyCta.buttons}
        />
      </div>
    </>
  );
};

export default CaseStudies;